import React from 'react'
import { useNavigate } from 'react-router-dom'
import { BookOpen, ArrowLeft } from 'lucide-react'
import { sage, neutral, fonts, leather, paper } from '../styles/theme.js'

const SITE = 'mycookbook.io'
const APP  = "My Cookbook"
const EMAIL = 'privacy@mycookbook.io'
const UPDATED = 'April 2026'

export default function Privacy() {
  const navigate = useNavigate()

  return (
    <div style={{ minHeight: '100vh', background: paper.bg, fontFamily: fonts.serif }}>
      {/* Header */}
      <div style={{
        background: `linear-gradient(160deg, ${sage.dark}, ${sage.mid})`,
        borderBottom: `2px solid white`,
        padding: '20px 32px',
        display: 'flex', alignItems: 'center', gap: 16,
      }}>
        <BookOpen size={28} color={leather.gold} />
        <span style={{ fontFamily: fonts.cursive, fontSize: 26, color: leather.gold, flex: 1 }}>
          {APP}
        </span>
        <button
          onClick={() => navigate(-1)}
          style={{
            display: 'flex', alignItems: 'center', gap: 6,
            background: 'none', border: `1px solid rgba(255,255,255,0.5)`,
            borderRadius: 6, padding: '6px 12px', cursor: 'pointer',
            color: leather.goldLight, fontFamily: fonts.serif, fontSize: 14,
          }}
        >
          <ArrowLeft size={14} /> Back
        </button>
      </div>

      {/* Content */}
      <div style={{ maxWidth: 780, margin: '0 auto', padding: '48px 32px 80px' }}>
        <h1 style={{ fontFamily: fonts.cursive, fontSize: 42, color: neutral[900], marginBottom: 6 }}>
          Privacy Policy
        </h1>
        <p style={{ fontSize: 15, color: neutral[500], fontStyle: 'italic', marginBottom: 40 }}>
          Last updated: {UPDATED}
        </p>

        <div style={{ height: 1, background: `linear-gradient(90deg, ${neutral[300]}, transparent)`, marginBottom: 40 }} />

        <Section title="1. Who We Are">
          <p>{APP} ("{SITE}") is a personal recipe management application. We are a small, family-run
          service. If you have questions about this policy, contact us at <a href={`mailto:${EMAIL}`}
          style={{ color: neutral[900] }}>{EMAIL}</a>.</p>
        </Section>

        <Section title="2. Information We Collect">
          <p>We collect only what is necessary to provide the service:</p>
          <ul>
            <li><strong>Account information</strong> — your name, email address, and profile photo,
            provided by Google or Facebook when you sign in via OAuth. We never see your password.</li>
            <li><strong>Recipe data</strong> — recipes, ingredients, steps, notes, and favorites that
            you create or import within the app.</li>
            <li><strong>Billing information</strong> — if you subscribe, payment is processed entirely
            by Stripe. We store only your Stripe customer ID; we never see or store your card number.</li>
            <li><strong>Usage data</strong> — basic server logs (timestamps, IP addresses) for security
            and debugging. These are not sold or shared.</li>
          </ul>
        </Section>

        <Section title="3. How We Use Your Information">
          <ul>
            <li>To authenticate you and maintain your session</li>
            <li>To store and retrieve your recipes</li>
            <li>To process subscription payments via Stripe</li>
            <li>To send transactional emails (e.g. import completion notifications) via SendGrid</li>
            <li>To power the AI cooking assistant (your recipe content is sent to Anthropic's Claude
            API to answer your questions; it is not used to train models)</li>
          </ul>
          <p>We do not sell your data, run advertising, or share your information with third parties
          beyond the service providers listed above.</p>
        </Section>

        <Section title="4. Third-Party Services">
          <p>We use the following third-party providers, each with their own privacy policies:</p>
          <ul>
            <li><strong>Google OAuth</strong> — sign-in authentication</li>
            <li><strong>Facebook OAuth</strong> — sign-in authentication</li>
            <li><strong>Stripe</strong> — subscription billing</li>
            <li><strong>Anthropic</strong> — AI cooking assistant (Chef's Oracle)</li>
            <li><strong>SendGrid</strong> — transactional email</li>
            <li><strong>Railway / Vercel</strong> — hosting and infrastructure</li>
          </ul>
        </Section>

        <Section title="5. Data Retention">
          <p>We retain your account and recipe data for as long as your account is active. If you
          request deletion of your account, all associated data (recipes, notes, conversation history)
          is permanently removed within 30 days.</p>
        </Section>

        <Section title="6. Your Rights">
          <p>You have the right to:</p>
          <ul>
            <li>Access the data we hold about you</li>
            <li>Correct inaccurate data</li>
            <li>Request deletion of your account and all associated data</li>
            <li>Export your recipe data</li>
          </ul>
          <p>To exercise any of these rights, email us at <a href={`mailto:${EMAIL}`}
          style={{ color: neutral[900] }}>{EMAIL}</a> or use our{' '}
          <a href="/data-deletion" style={{ color: neutral[900] }}>data deletion page</a>.</p>
        </Section>

        <Section title="7. Cookies & Storage">
          <p>We store your authentication token in your browser's <code>localStorage</code> to keep
          you signed in. We do not use advertising cookies or tracking pixels.</p>
        </Section>

        <Section title="8. Children's Privacy">
          <p>This app is intended for family use. We do not knowingly collect data from children
          under 13 without parental consent. Family members using the app under parental supervision
          are permitted.</p>
        </Section>

        <Section title="9. Changes to This Policy">
          <p>We may update this policy occasionally. When we do, we'll update the date at the top
          of this page. Continued use of the app after changes constitutes acceptance.</p>
        </Section>

        <Section title="10. Contact">
          <p>Questions or concerns? Email us at{' '}
          <a href={`mailto:${EMAIL}`} style={{ color: neutral[900] }}>{EMAIL}</a>.</p>
        </Section>
      </div>
    </div>
  )
}

function Section({ title, children }) {
  return (
    <div style={{ marginBottom: 36 }}>
      <h2 style={{
        fontFamily: fonts.serif, fontSize: 22, fontWeight: 700,
        color: neutral[900], marginBottom: 12,
        paddingBottom: 6, borderBottom: `1px solid ${neutral[300]}`,
      }}>
        {title}
      </h2>
      <div style={{
        fontSize: 17, color: neutral[700], lineHeight: 1.75,
        display: 'flex', flexDirection: 'column', gap: 10,
      }}>
        {children}
      </div>
    </div>
  )
}
